import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { API } from "../../shared/apiRoutes";
import HttpRoutingService from "../../shared/services/httpRoutingService";

export interface ReturnReason {
    id: number;
    returnReasons: string;
    codeField: number | null;
    isEnable: boolean;
    isDefault: boolean;
    isDeleted: boolean;
}

export interface ReturnReasonItem {
    id?: number; // present only for rows already saved → backend updates instead of inserting
    returnReasons: string;
    codeField?: number;
    isEnable: boolean;
    isDefault: boolean;
    isDeleted: boolean;
}

export interface ReturnReasonsResponse {
    returnReasonsData: ReturnReason[];
    code: string;
    message: string;
    success: boolean;
}

export interface FetchReturnReasonPayload {
    storeId: number | string;
    params?: any;
}

export interface UpdateReturnReasonPayload {
    storeId: number | string;
    data: ReturnReasonItem[];
}

export interface ResetReturnReasonPayload {
    storeId: number;
    id: number;
    isDefault: boolean; // required query param of v2 DELETE return-reason/{id}
}

interface CommonType {
    loading: boolean;
    error: string;
}
interface DeleteReturnReasonResponse {
    returnReasonsData: ReturnReason[];
    code: string;
    message: string;
    success: boolean;
}

interface InitialStateProps {
    loading: boolean;
    error: string;
    getReturnReasons: CommonType & { value: ReturnReasonsResponse | null };
    updateReturnReasons: CommonType & { value: any | null };
    deleteReturnReason: CommonType & { value: any | null };
    getTipSettings: CommonType & { value: any | null };
    updateTipSettings: CommonType & { value: any | null };
}

const initialState: InitialStateProps = {
    loading: false,
    error: '',
    getReturnReasons: { value: null, loading: false, error: '' },
    updateReturnReasons: { value: null, loading: false, error: '' },
    deleteReturnReason: { value: null, loading: false, error: '' },
    getTipSettings: { value: null, loading: false, error: '' },
    updateTipSettings: { value: null, loading: false, error: '' },
};

/**
 * Fetch Return Reasons (GET /v2/stores/{storeId}/returnReasons)
 */
export const getReturnReasons = createAsyncThunk(
    "orderSettings/getReturnReasons",
    async ({ storeId, params }: FetchReturnReasonPayload) => {
        const url = HttpRoutingService.replaceUrl(API.returnReasons, { storeId });
        return await HttpRoutingService.getMethod<ReturnReasonsResponse>(url, params, "v2/");
    }
);

/**
 * Update Return Reasons (PUT /v2/stores/{storeId}/returnReasons)
 */
export const updateReturnReasons = createAsyncThunk(
    "orderSettings/updateReturnReasons",
    async ({ storeId, data }: UpdateReturnReasonPayload) => {
        const url = HttpRoutingService.replaceUrl(API.returnReasons, { storeId });
        return await HttpRoutingService.putMethod<DeleteReturnReasonResponse>(url, data, undefined, "v2/");
    }
);

/**
 * Reset Return Reasons (DELETE /v2/stores/{storeId}/returnReasons/{id})
 */
export const deleteReturnReason = createAsyncThunk(
    "orderSettings/deleteReturnReason",
    async ({ storeId, id, isDefault }: ResetReturnReasonPayload) => {
        const url = HttpRoutingService.replaceUrl(API.deleteReturnReason, { storeId, id });
        return await HttpRoutingService.deleteMethod<DeleteReturnReasonResponse>(url, { isDefault }, "v2/");
    }
);

/**
 * Fetch Tip Settings (GET /v2/stores/{storeId}/tips)
 */
export const getTipSettings = createAsyncThunk(
    "orderSettings/getTipSettings",
    async ({ storeId }: { storeId: number | string }) => {
        const url = HttpRoutingService.replaceUrl(API.getTipDetails, { storeId });
        return await HttpRoutingService.getMethod<any>(url, undefined, "v2/");
    }
);

/**
 * Update Tip Settings (PUT /v2/stores/{storeId}/tips)
 */
export const updateTipSettings = createAsyncThunk(
    "orderSettings/updateTipSettings",
    async ({ storeId, data }: { storeId: number | string; data: any }) => {
        const url = HttpRoutingService.replaceUrl(API.getTipDetails, { storeId });
        return await HttpRoutingService.putMethod<any>(url, data, undefined, "v2/");
    }
);

export const orderSettingsSlice = createSlice({
    name: "orderSettings",
    initialState,
    reducers: {

    },
    extraReducers: (builder) => {
        builder
            // Fetch Return Reasons
            .addCase(getReturnReasons.pending, (state) => {
                state.getReturnReasons.loading = true;
                state.getReturnReasons.error = '';
            })
            .addCase(getReturnReasons.fulfilled, (state, action) => {
                state.getReturnReasons.loading = false;
                state.getReturnReasons.value = action.payload;
            })
            .addCase(getReturnReasons.rejected, (state, action) => {
                state.getReturnReasons.loading = false;
                state.getReturnReasons.error = (action?.error?.message as string) || "Failed to fetch return reasons";
            })

            // Update Return Reasons
            .addCase(updateReturnReasons.pending, (state) => {
                state.updateReturnReasons.loading = true;
                state.updateReturnReasons.error = '';
            })
            .addCase(updateReturnReasons.fulfilled, (state, action) => {
                state.updateReturnReasons.loading = false;
                state.updateReturnReasons.value = action.payload;
            })
            .addCase(updateReturnReasons.rejected, (state, action) => {
                state.updateReturnReasons.loading = false;
                state.updateReturnReasons.error = (action?.error?.message as string) || "Failed to update return reasons";
            })

            // Reset / Delete Return Reasons
            .addCase(deleteReturnReason.pending, (state) => {
                state.deleteReturnReason.loading = true;
                state.deleteReturnReason.error = '';
            })
            .addCase(deleteReturnReason.fulfilled, (state, action) => {
                state.deleteReturnReason.loading = false;
                state.deleteReturnReason.value = action.payload;
            })
            .addCase(deleteReturnReason.rejected, (state, action) => {
                state.deleteReturnReason.loading = false;
                state.deleteReturnReason.error = (action?.error?.message as string) || "Failed to reset return reasons";
            })

            // Fetch Tip Settings
            .addCase(getTipSettings.pending, (state) => {
                state.getTipSettings.loading = true;
                state.getTipSettings.error = '';
            })
            .addCase(getTipSettings.fulfilled, (state, action) => {
                state.getTipSettings.loading = false;
                state.getTipSettings.value = action.payload;
            })
            .addCase(getTipSettings.rejected, (state, action) => {
                state.getTipSettings.loading = false;
                state.getTipSettings.error = (action?.error?.message as string) || "Failed to fetch tip settings";
            })

            // Update Tip Settings
            .addCase(updateTipSettings.pending, (state) => {
                state.updateTipSettings.loading = true;
                state.updateTipSettings.error = '';
            })
            .addCase(updateTipSettings.fulfilled, (state, action) => {
                state.updateTipSettings.loading = false;
                state.updateTipSettings.value = action.payload;
            })
            .addCase(updateTipSettings.rejected, (state, action) => {
                state.updateTipSettings.loading = false;
                state.updateTipSettings.error = (action?.error?.message as string) || "Failed to update tip settings";
            });
    }
});

export default orderSettingsSlice.reducer;
